/**
 * FeedbackButton — sits next to the notification bell. Opens a small form to
 * report a bug or suggest an improvement, with an optional screenshot captured
 * from the current page view (html2canvas). On submit, a quick thank-you.
 *
 * The submit is OPTIMISTIC: the modal closes immediately and the callable runs
 * in the background. On failure a toast appears (via BackgroundSubmitProvider)
 * with a Retry button that reopens the modal with the user's text + screenshot
 * preserved.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { CALLABLES, type FeedbackType } from '@daaily/shared';
import { useProfile } from '../profile/ProfileProvider';
import { callSubmitFeedback } from './callables';
import { useFireAndForget } from '../components/BackgroundSubmit';
import { HeartHandshake, MessageSquare, X } from 'lucide-react';
import { Icon } from '../design/Icon';
import { segGroup, segItemOff, segItemOn } from '../design/ui';

/** Page ground of the current theme (tokens.css --bg), for the screenshot fill. */
function pageGround(): string {
  const ch = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim().split(/\s+/);
  return ch.length === 3 ? `rgb(${ch.join(', ')})` : '#0a0a0a';
}

const ANNOUNCEMENT_KEY = 'feedback-button-2026-06';
const callDismissAnnouncement = httpsCallable<{ key: string }, { ok: true }>(
  functions,
  CALLABLES.DISMISS_ANNOUNCEMENT,
);

/** Window event that opens the feedback form from anywhere, e.g. a modal's "Report a problem" (Oct 2026):
 *  modals cover the top bar, so the button itself is unreachable while one is open. */
export const FEEDBACK_OPEN_EVENT = 'daaily:feedback:open';
export function openFeedback(): void {
  window.dispatchEvent(new Event(FEEDBACK_OPEN_EVENT));
}

export function FeedbackButton() {
  const { state } = useProfile();
  const fireAndForget = useFireAndForget();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener(FEEDBACK_OPEN_EVENT, on);
    return () => window.removeEventListener(FEEDBACK_OPEN_EVENT, on);
  }, []);
  // One-time announcement bubble — shows on every load/login until dismissed
  // (persisted per-user via the dismissedAnnouncements array).
  const dismissed =
    state.status === 'ready' && (state.user.dismissedAnnouncements ?? []).includes(ANNOUNCEMENT_KEY);
  const [announceHidden, setAnnounceHidden] = useState(false);
  const showAnnounce = state.status === 'ready' && !dismissed && !announceHidden && !open;

  const dismissAnnounce = () => {
    setAnnounceHidden(true);
    void callDismissAnnouncement({ key: ANNOUNCEMENT_KEY }).catch(() => undefined);
  };
  const [type, setType] = useState<FeedbackType>('bug');
  const [text, setText] = useState('');
  const [shot, setShot] = useState<string | null>(null); // data URL preview
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thanks, setThanks] = useState(false);

  const reset = () => {
    setType('bug');
    setText('');
    setShot(null);
    setError(null);
    setThanks(false);
  };

  const takeScreenshot = async () => {
    setError(null);
    setCapturing(true); // hides the modal so we capture the page behind it
    try {
      await new Promise((r) => window.setTimeout(r, 120)); // let the modal hide paint
      // Lazy: html2canvas is ~400 KB and the button lives in the always-loaded shell.
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        backgroundColor: pageGround(),
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
      });
      setShot(canvas.toDataURL('image/jpeg', 0.7));
    } catch {
      setError('Could not capture the screen. You can still submit without a screenshot.');
    } finally {
      setCapturing(false);
    }
  };

  const submit = () => {
    setError(null);
    // Capture the payload now, before we reset state.
    const base = shot ? shot.slice(shot.indexOf(',') + 1) : undefined;
    const payload = {
      type,
      text: text.trim(),
      pageUrl: window.location.pathname + window.location.search,
      ...(base ? { screenshotBase64: base, screenshotMimeType: 'image/jpeg' as const } : {}),
    };
    // Snapshot for retry (so the user can reopen with their text + screenshot choice intact).
    const savedType = type;
    const savedText = text;
    const savedShot = shot;

    // Acknowledge immediately — show thank-you, close after a moment.
    setThanks(true);
    window.setTimeout(() => {
      setOpen(false);
      reset();
    }, 1800);

    // Fire in background; on failure show a toast with Retry.
    fireAndForget(() => callSubmitFeedback(payload), {
      onRetry: () => {
        // Restore the form with the saved content and reopen.
        setType(savedType);
        setText(savedText);
        setShot(savedShot);
        setThanks(false);
        setError(null);
        setOpen(true);
      },
    });
  };

  return (
    <>
      <span className="relative inline-flex">
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(true);
          }}
          title="Send feedback"
          aria-label="Send feedback"
          className="flex items-center text-fg-muted hover:text-fg transition-colors duration-fast"
        >
          <Icon as={MessageSquare} size={20} />
        </button>

        {showAnnounce && (
          <div className="absolute right-0 top-full mt-3 z-50 w-64">
            {/* tail pointing up at the button */}
            <div className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-surface border-l border-t border-line" />
            <div className="relative rounded-lg border border-line bg-surface text-fg shadow-lg p-3">
              <button
                type="button"
                onClick={dismissAnnounce}
                aria-label="Dismiss"
                className="absolute top-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2"
              >
                <Icon as={X} size={14} />
              </button>
              <p className="text-2xs font-semibold uppercase text-brand">New feature</p>
              <p className="text-sm mt-1">Submit feedback and bugs directly through the tool.</p>
              <p className="text-sm mt-1 text-fg-muted">Thank you.</p>
            </div>
          </div>
        )}
      </span>

      {/* Portalled to <body>: the top bar has backdrop-blur, which makes it the
          containing block for `fixed` descendants, so an in-place overlay was
          clipped to the 56px header strip (Sep 2026). */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-modal bg-app/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ visibility: capturing ? 'hidden' : 'visible' }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              reset();
            }
          }}
        >
          <div className="rounded-xl border border-line bg-surface shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto p-5 text-fg">
            {thanks ? (
              <div className="py-8 text-center">
                <Icon as={HeartHandshake} size={32} className="mx-auto mb-2 text-brand" />
                <p className="text-sm font-medium">Thanks for your feedback!</p>
                <p className="text-xs text-fg-muted mt-1">We’ll take a look.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-sans tracking-normal text-base font-semibold">Send feedback</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      reset();
                    }}
                    className="-mr-1.5 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2"
                    aria-label="Close"
                  >
                    <Icon as={X} />
                  </button>
                </div>

                <label className="block text-xs text-fg-muted mb-1">Type</label>
                <div className={`${segGroup} mb-3`}>
                  {(['bug', 'improvement'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={type === t ? segItemOn : segItemOff}
                    >
                      {t === 'bug' ? 'Bug' : 'Improvement suggestion'}
                    </button>
                  ))}
                </div>

                <label className="block text-xs text-fg-muted mb-1">Details</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  placeholder="What happened, or what would you improve?"
                  className="w-full mb-3"
                />

                <div className="mb-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void takeScreenshot()}
                      className="btn-secondary text-xs !py-1.5 !px-3"
                    >
                      {shot ? 'Retake screenshot' : 'Take screenshot'}
                    </button>
                    {shot && (
                      <button
                        type="button"
                        onClick={() => setShot(null)}
                        className="text-xs text-fg-muted hover:text-danger"
                      >
                        remove
                      </button>
                    )}
                  </div>
                  {shot && (
                    <img
                      src={shot}
                      alt="Screenshot preview"
                      className="mt-2 w-full max-h-40 object-contain rounded border border-line"
                    />
                  )}
                </div>

                {error && <p className="text-sm text-danger mb-2">{error}</p>}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      reset();
                    }}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => submit()}
                    disabled={text.trim().length === 0}
                    className="btn-primary text-sm disabled:opacity-50"
                  >
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
